import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  ListChecks, Plus, ChevronDown, ChevronRight, Copy, Check,
  Lightbulb, Target, MessageSquareQuote, Wrench, ClipboardCheck, Clock, Wallet,
} from 'lucide-react';

/**
 * Tareas sugeridas — el pendiente que hasta hoy vivía en una conversación.
 *
 * Nació de un caso real: los sitios de clientes no pueden servirse en
 * `<cliente>.sites.cavernicolapp.com` porque el certificado de Cloudflare cubre
 * un solo nivel de subdominio. Se decidió pagar el certificado avanzado cuando
 * haya volumen. Sin un lugar donde guardarla, esa decisión —con su motivo, su
 * costo y los pasos exactos— se perdía al cerrar el chat.
 *
 * Por eso cada tarea se cuenta en cinco bloques y no en una descripción libre:
 * quien las lee necesita entender QUÉ es y PARA QUÉ sirve antes de ver un
 * comando, y necesita que le digan qué mirar DESPUÉS de correrlo.
 */

const ESTADOS = {
  pendiente: { texto: 'Pendiente', clase: 'bg-slate-100 text-slate-700 border-slate-200' },
  en_progreso: { texto: 'En curso', clase: 'bg-blue-50 text-blue-700 border-blue-200' },
  hecha: { texto: 'Hecha', clase: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  descartada: { texto: 'Descartada', clase: 'bg-slate-50 text-slate-400 border-slate-200' },
};

const PRIORIDADES = {
  alta: { texto: 'Alta', clase: 'bg-red-50 text-red-600 border-red-200' },
  media: { texto: 'Media', clase: 'bg-amber-50 text-amber-700 border-amber-200' },
  baja: { texto: 'Baja', clase: 'bg-slate-50 text-slate-500 border-slate-200' },
};

const Etiqueta = ({ mapa, valor }) => {
  const d = mapa[valor] || { texto: valor, clase: 'bg-slate-100 text-slate-600 border-slate-200' };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${d.clase}`}>
      {d.texto}
    </span>
  );
};

/** Un bloque de texto largo. El de los pasos va en monoespaciada y se copia. */
const Bloque = ({ icono: Icono, titulo, texto, copiable }) => {
  const [copiado, setCopiado] = useState(false);
  if (!texto || !texto.trim()) return null;

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      toast.error('Tu navegador no dejó copiar. Selecciona el texto y usa Ctrl+C.');
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h4 className="flex items-center gap-2 text-sm font-semibold text-[#0F172A]">
          <Icono className="w-4 h-4 text-[#F72585]" /> {titulo}
        </h4>
        {copiable && (
          <button onClick={copiar}
                  className="text-xs text-[#64748B] hover:text-[#0F172A] flex items-center gap-1">
            {copiado ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            {copiado ? 'Copiado' : 'Copiar'}
          </button>
        )}
      </div>
      <p className={`whitespace-pre-wrap text-sm leading-relaxed rounded-lg p-3 ${
        copiable ? 'font-mono text-[13px] bg-[#0F172A] text-[#E2E8F0] overflow-x-auto'
                 : 'text-[#334155] bg-[#F8FAFC] border border-[#E2E8F0]'}`}>
        {texto}
      </p>
    </div>
  );
};

const Tarjeta = ({ tarea, onCambiarEstado }) => {
  const [abierta, setAbierta] = useState(false);
  const apagada = tarea.estado === 'hecha' || tarea.estado === 'descartada';

  return (
    <div className={`bg-white rounded-xl border ${apagada ? 'border-[#E2E8F0] opacity-70'
                                                          : 'border-[#CBD5E1]'} overflow-hidden`}>
      <button onClick={() => setAbierta(!abierta)}
              className="w-full text-left p-4 hover:bg-[#F8FAFC] transition-colors">
        <div className="flex items-start gap-3">
          {abierta ? <ChevronDown className="w-5 h-5 text-[#94A3B8] shrink-0 mt-0.5" />
                   : <ChevronRight className="w-5 h-5 text-[#94A3B8] shrink-0 mt-0.5" />}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h3 className={`font-semibold text-[#0F172A] ${apagada ? 'line-through' : ''}`}>
                {tarea.titulo}
              </h3>
              <Etiqueta mapa={ESTADOS} valor={tarea.estado} />
              <Etiqueta mapa={PRIORIDADES} valor={tarea.prioridad} />
            </div>
            {tarea.resumen && (
              <p className="text-sm text-[#475569] leading-snug">{tarea.resumen}</p>
            )}
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-[#64748B]">
              {tarea.disparador && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {tarea.disparador}
                </span>
              )}
              {tarea.costo && (
                <span className="flex items-center gap-1">
                  <Wallet className="w-3 h-3" /> {tarea.costo}
                </span>
              )}
            </div>
          </div>
        </div>
      </button>

      {abierta && (
        <div className="px-4 pb-4 pt-1 space-y-5 border-t border-[#F1F5F9]">
          <Bloque icono={Lightbulb} titulo="Qué es" texto={tarea.que_es} />
          <Bloque icono={Target} titulo="Para qué sirve" texto={tarea.para_que_sirve} />
          <Bloque icono={MessageSquareQuote} titulo="Dicho en corto" texto={tarea.ejemplo} />
          <Bloque icono={Wrench} titulo="Qué hay que hacer" texto={tarea.fix_propuesto} copiable />
          <Bloque icono={ClipboardCheck} titulo="Cómo saber si funcionó"
                  texto={tarea.verificacion} />
          {tarea.notas && (
            <Bloque icono={MessageSquareQuote} titulo="Notas" texto={tarea.notas} />
          )}

          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[#F1F5F9]">
            <span className="text-xs text-[#64748B] mr-1">Marcar como:</span>
            {Object.entries(ESTADOS).map(([valor, d]) => (
              <Button key={valor} size="sm"
                      variant={tarea.estado === valor ? 'default' : 'outline'}
                      disabled={tarea.estado === valor}
                      onClick={() => onCambiarEstado(tarea, valor)}>
                {d.texto}
              </Button>
            ))}
            {tarea.origen && (
              <span className="text-xs text-[#94A3B8] ml-auto">De: {tarea.origen}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const VACIA = {
  titulo: '', resumen: '', que_es: '', para_que_sirve: '', ejemplo: '',
  fix_propuesto: '', verificacion: '', disparador: '', costo: '',
  prioridad: 'media', categoria: 'plataforma', origen: '',
};

export default function Roadmap() {
  const [tareas, setTareas] = useState([]);
  const [conteo, setConteo] = useState({});
  const [cargando, setCargando] = useState(true);
  const [nueva, setNueva] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/roadmap');
      setTareas(data.tareas || []);
      setConteo(data.conteo || {});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const cambiarEstado = async (tarea, estado) => {
    try {
      await api.patch(`/admin/roadmap/${tarea.id}`, { estado });
      toast.success(`«${tarea.titulo}» quedó como ${ESTADOS[estado].texto.toLowerCase()}`);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const crear = async () => {
    if (!nueva.titulo.trim() || nueva.titulo.trim().length < 3) {
      toast.error('Ponle un título de al menos 3 letras');
      return;
    }
    setGuardando(true);
    try {
      await api.post('/admin/roadmap', nueva);
      toast.success('Tarea agregada');
      setNueva(null);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGuardando(false);
    }
  };

  const campo = (k, v) => setNueva((n) => ({ ...n, [k]: v }));

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] flex items-center gap-2">
            <ListChecks className="w-6 h-6 text-[#F72585]" /> Tareas sugeridas
          </h1>
          <p className="text-sm text-[#475569] mt-1 max-w-2xl">
            Cosas que hoy no se hacen a propósito —porque cuestan dinero, porque hace
            falta más volumen, o porque todavía no urgen— con todo lo necesario para
            retomarlas: qué es, para qué sirve, los pasos exactos y cómo comprobar
            que quedó bien.
          </p>
        </div>
        <Button onClick={() => setNueva({ ...VACIA })}
                className="bg-[#F72585] hover:bg-[#D81B70]">
          <Plus className="w-4 h-4 mr-1" /> Agregar
        </Button>
      </div>

      {!cargando && (
        <div className="flex flex-wrap gap-3 text-sm">
          {Object.entries(ESTADOS).map(([valor, d]) => (
            <div key={valor} className="px-3 py-1.5 rounded-lg bg-white border border-[#E2E8F0]">
              <span className="font-semibold text-[#0F172A]">{conteo[valor] || 0}</span>
              <span className="text-[#64748B] ml-1.5">{d.texto.toLowerCase()}</span>
            </div>
          ))}
        </div>
      )}

      {cargando ? (
        <p className="text-sm text-[#64748B]">Cargando…</p>
      ) : tareas.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-[#CBD5E1] p-10 text-center">
          <ListChecks className="w-10 h-10 text-[#CBD5E1] mx-auto mb-3" />
          <p className="text-[#475569] font-medium">Todavía no hay ninguna</p>
          <p className="text-sm text-[#64748B] mt-1">
            Aquí van las decisiones que se aplazan a propósito, para que no se olvide
            ni el motivo ni cómo retomarlas.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {tareas.map((t) => (
            <Tarjeta key={t.id} tarea={t} onCambiarEstado={cambiarEstado} />
          ))}
        </div>
      )}

      <Dialog open={!!nueva} onOpenChange={(v) => !v && setNueva(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nueva tarea sugerida</DialogTitle></DialogHeader>
          {nueva && (
            <div className="space-y-4">
              <div>
                <Label>Título</Label>
                <Input value={nueva.titulo} onChange={(e) => campo('titulo', e.target.value)}
                       placeholder="Contratar el certificado avanzado de Cloudflare" />
              </div>
              <div>
                <Label>En una línea</Label>
                <Input value={nueva.resumen} onChange={(e) => campo('resumen', e.target.value)}
                       placeholder="Qué pasa hoy por no haberla hecho" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Prioridad</Label>
                  <Select value={nueva.prioridad} onValueChange={(v) => campo('prioridad', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(PRIORIDADES).map(([v, d]) => (
                        <SelectItem key={v} value={v}>{d.texto}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Costo, si tiene</Label>
                  <Input value={nueva.costo} onChange={(e) => campo('costo', e.target.value)}
                         placeholder="Unos 10 USD al mes" />
                </div>
              </div>
              <div>
                <Label>¿Cuándo conviene hacerla?</Label>
                <Input value={nueva.disparador} onChange={(e) => campo('disparador', e.target.value)}
                       placeholder="Cuando haya varios clientes con sitio publicado" />
              </div>
              <div>
                <Label>Qué es</Label>
                <Textarea rows={4} value={nueva.que_es}
                          onChange={(e) => campo('que_es', e.target.value)}
                          placeholder="Explícalo sin jerga: qué pasa y por qué" />
              </div>
              <div>
                <Label>Para qué sirve</Label>
                <Textarea rows={3} value={nueva.para_que_sirve}
                          onChange={(e) => campo('para_que_sirve', e.target.value)}
                          placeholder="Qué gana el negocio al hacerla" />
              </div>
              <div>
                <Label>Dicho en corto</Label>
                <Textarea rows={3} value={nueva.ejemplo}
                          onChange={(e) => campo('ejemplo', e.target.value)}
                          placeholder="La misma idea con una comparación cotidiana" />
              </div>
              <div>
                <Label>Qué hay que hacer</Label>
                <Textarea rows={5} className="font-mono text-sm" value={nueva.fix_propuesto}
                          onChange={(e) => campo('fix_propuesto', e.target.value)}
                          placeholder="Los pasos o el comando, en orden" />
              </div>
              <div>
                <Label>Cómo saber si funcionó</Label>
                <Textarea rows={4} value={nueva.verificacion}
                          onChange={(e) => campo('verificacion', e.target.value)}
                          placeholder="Qué mirar DESPUÉS de correrlo, y qué querría decir si falla" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setNueva(null)}>Cancelar</Button>
                <Button onClick={crear} disabled={guardando}
                        className="bg-[#F72585] hover:bg-[#D81B70]">
                  {guardando ? 'Guardando…' : 'Guardar'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
